import { ApiEnvelope, AuthPayload, PaginationPayload } from "../types";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api/v1";
const AUTH_STORAGE_KEY = "eventena_admin_auth";
export const DEFAULT_PAYMENT_QR_IMAGE_URL = "/assets/images/payment-qr-default.png";

export function normalizeApiAssetPath(value?: string | null) {
  const raw = String(value || "").trim();

  if (!raw) {
    return "";
  }

  if (/^https?:\/\//i.test(raw)) {
    try {
      const apiOrigin = API_BASE.replace(/\/api(?:\/.*)?$/i, "").replace(/\/$/, "");
      const parsedUrl = new URL(raw);

      if (/^\/uploads\//i.test(parsedUrl.pathname)) {
        return parsedUrl.pathname;
      }

      if (parsedUrl.origin === apiOrigin && /^\/assets\//i.test(parsedUrl.pathname)) {
        return parsedUrl.pathname;
      }
    } catch (error) {
      return raw;
    }
  }

  if (/^(https?:|data:|blob:|\/\/)/i.test(raw)) {
    return raw;
  }

  if (raw.startsWith("/")) {
    return raw;
  }

  if (raw.includes("/")) {
    return `/${raw}`;
  }

  return `/assets/images/${raw}`;
}

export function resolveApiAssetUrl(value?: string | null) {
  const raw = normalizeApiAssetPath(value);

  if (!raw) {
    return "";
  }

  if (/^(https?:|data:|blob:|\/\/)/i.test(raw)) {
    return raw;
  }

  const apiOrigin = API_BASE.replace(/\/api(?:\/.*)?$/i, "").replace(/\/$/, "");
  return `${apiOrigin}${raw.startsWith("/") ? raw : `/${raw}`}`;
}

function getAuthToken() {
  const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as AuthPayload;
    return parsed.token;
  } catch (error) {
    return null;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers || {});

  if (!headers.has("Content-Type") && options.body) {
    headers.set("Content-Type", "application/json");
  }

  const token = getAuthToken();

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    cache: options.cache || "no-store"
  });

  const text = await response.text();
  const json = text ? JSON.parse(text) as ApiEnvelope<T> : null;

  if (!response.ok || !json?.success) {
    throw new Error(json?.message || `Request failed with status ${response.status}`);
  }

  return json.data;
}

export const adminApi = {
  baseUrl: API_BASE,
  authStorageKey: AUTH_STORAGE_KEY,
  login: (payload: { email: string; password: string }) =>
    request<{ token: string; user: AuthPayload["user"] }>("/auth/login", {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  me: () => request<AuthPayload["user"]>("/auth/me"),
  get: <T>(path: string) => request<T>(path),
  getWithSignal: <T>(path: string, signal: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, {
      method: "POST",
      body: JSON.stringify(body)
    }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, {
      method: "PUT",
      body: JSON.stringify(body)
    }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, {
      method: "PATCH",
      body: JSON.stringify(body)
    }),
  delete: <T>(path: string) =>
    request<T>(path, {
      method: "DELETE"
    }),
  listResource: <T>(resource: string, query = "") =>
    request<PaginationPayload<T>>(`/admin/${resource}${query}`),
  getResourceById: <T>(resource: string, id: number | string, secondaryId?: number | string) =>
    request<T>(`/admin/${resource}/${id}${secondaryId !== undefined ? `/${secondaryId}` : ""}`),
  createResource: <T>(resource: string, body: unknown) =>
    request<T>(`/admin/${resource}`, {
      method: "POST",
      body: JSON.stringify(body)
    }),
  updateResource: <T>(resource: string, id: number | string, body: unknown, secondaryId?: number | string) =>
    request<T>(`/admin/${resource}/${id}${secondaryId !== undefined ? `/${secondaryId}` : ""}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    }),
  deleteResource: <T>(resource: string, id: number | string, secondaryId?: number | string) =>
    request<T>(`/admin/${resource}/${id}${secondaryId !== undefined ? `/${secondaryId}` : ""}`, {
      method: "DELETE"
    })
};
